import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, MapPin, Clock, Calendar, Phone, MessageSquare, Video, User, ChevronLeft, Award, Briefcase, Image, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { formatCurrency } from '../utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LawyerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lawyer, setLawyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryModal, setGalleryModal] = useState({ open: false, photo: null });

  const getPhotoUrl = (path) => {
    if (!path) return null;
    return `${BACKEND_URL}/api/lawyers/photos/${path}`;
  };

  const fetchLawyer = useCallback(async () => {
    try {
      const res = await api.get(`/lawyers/${id}`);
      setLawyer(res.data);
    } catch (err) {
      toast.error('Lawyer not found');
      navigate('/lawyers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchLawyer();
  }, [fetchLawyer]);

  const handleBookConsultation = () => {
    if (!user) {
      toast.error('Please login to book a consultation');
      navigate('/login');
      return;
    }
    if (user.role !== 'client') {
      toast.error('Only clients can book consultations');
      return;
    }
    navigate(`/book/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/4 mb-8" />
            <div className="bg-white rounded-2xl p-8">
              <div className="flex gap-6">
                <div className="w-32 h-32 bg-slate-200 rounded-2xl" />
                <div className="flex-1">
                  <div className="h-8 bg-slate-200 rounded w-1/2 mb-4" />
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!lawyer) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Back Button */}
        <Link 
          to="/lawyers" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
          data-testid="back-to-search-link"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Search
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8 border border-slate-200"
            >
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-32 h-32 bg-slate-100 rounded-2xl flex items-center justify-center text-4xl font-bold text-slate-600 shrink-0 overflow-hidden">
                  {lawyer.profile_photo ? (
                    <img
                      src={getPhotoUrl(lawyer.profile_photo)}
                      alt={lawyer.user?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    lawyer.user?.name?.charAt(0) || 'L'
                  )}
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{lawyer.user?.name}</h1>
                  <p className="text-slate-500 mt-1">{lawyer.qualification}</p>
                  
                  <div className="flex flex-wrap items-center gap-4 mt-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                      <span className="font-semibold">{lawyer.rating || 0}</span>
                      <span className="text-slate-400">({lawyer.review_count || 0} reviews)</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-600">
                      <Briefcase className="w-5 h-5" />
                      <span>{lawyer.years_of_experience} years</span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-600">
                      <MapPin className="w-5 h-5" />
                      <span>{lawyer.user?.location || 'Not specified'}</span>
                    </div>
                  </div>

                  {/* Mobile Number - Visible only to logged-in clients */}
                  {user && user.role === 'client' && lawyer.user?.mobile && (
                    <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Phone className="w-5 h-5 text-emerald-600" />
                          <span className="font-medium text-emerald-800">
                            {lawyer.user.mobile}
                          </span>
                        </div>
                        <a 
                          href={`tel:${lawyer.user.mobile}`}
                          className="px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2"
                          data-testid="call-lawyer-btn"
                        >
                          <Phone className="w-4 h-4" />
                          Call Now
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Prompt to login to see contact */}
                  {!user && (
                    <div className="mt-4 p-3 bg-slate-100 border border-slate-200 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Phone className="w-5 h-5" />
                          <span className="text-sm">Login to view contact number</span>
                        </div>
                        <Link 
                          to="/login"
                          className="px-4 py-2 bg-amber-600 text-white rounded-full text-sm font-medium hover:bg-amber-700 transition-colors"
                          data-testid="login-to-see-contact"
                        >
                          Login
                        </Link>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-4">
                    {lawyer.specializations?.map(spec => (
                      <span key={spec} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Tabs */}
            <Tabs defaultValue="about" className="bg-white rounded-2xl border border-slate-200">
              <TabsList className="w-full border-b border-slate-200 rounded-t-2xl">
                <TabsTrigger value="about" className="flex-1" data-testid="tab-about">About</TabsTrigger>
                {lawyer.gallery?.length > 0 && (
                  <TabsTrigger value="gallery" className="flex-1" data-testid="tab-gallery">Gallery</TabsTrigger>
                )}
                <TabsTrigger value="reviews" className="flex-1" data-testid="tab-reviews">Reviews</TabsTrigger>
              </TabsList>
              
              <TabsContent value="about" className="p-6">
                <div className="space-y-6">
                  {lawyer.bio && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">About</h3>
                      <p className="text-slate-600">{lawyer.bio}</p>
                    </div>
                  )}
                  
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Bar Council Registration</h3>
                    <p className="text-slate-600">{lawyer.bar_council_number}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Languages</h3>
                    <div className="flex flex-wrap gap-2">
                      {lawyer.languages?.map(lang => (
                        <span key={lang} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm">
                          {lang}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Practice Areas</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {lawyer.specializations?.map(spec => (
                        <div key={spec} className="flex items-center gap-2 text-slate-600">
                          <Award className="w-4 h-4 text-amber-500" />
                          <span>{spec}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Office Info */}
                  {(lawyer.office_address || lawyer.office_phone || lawyer.website) && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Office Information</h3>
                      <div className="space-y-2">
                        {lawyer.office_address && (
                          <div className="flex items-start gap-2 text-slate-600">
                            <MapPin className="w-4 h-4 mt-1 text-slate-400" />
                            <span>{lawyer.office_address}</span>
                          </div>
                        )}
                        {lawyer.office_phone && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span>{lawyer.office_phone}</span>
                          </div>
                        )}
                        {lawyer.website && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Globe className="w-4 h-4 text-slate-400" />
                            <a href={lawyer.website} target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">
                              {lawyer.website}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Gallery Tab */}
              {lawyer.gallery?.length > 0 && (
                <TabsContent value="gallery" className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {lawyer.gallery.map((photo, idx) => (
                      <div
                        key={photo.id}
                        className="aspect-square rounded-xl overflow-hidden bg-slate-100 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setGalleryModal({ open: true, photo })}
                        data-testid={`gallery-photo-${idx}`}
                      >
                        <img
                          src={getPhotoUrl(photo.path)}
                          alt={photo.caption || 'Gallery photo'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}

              <TabsContent value="reviews" className="p-6">
                {lawyer.reviews?.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-500">No reviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {lawyer.reviews?.map((review, idx) => (
                      <div key={review.id || idx} className="p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{review.client_name}</p>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`w-4 h-4 ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} 
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="mt-3 text-slate-600">{review.comment}</p>
                        <p className="mt-2 text-xs text-slate-400">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Booking */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 border border-slate-200 sticky top-24"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Book Consultation</h3>
              
              <div className="bg-amber-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-500">Consultation Fee</p>
                <p className="text-3xl font-bold text-slate-900">{formatCurrency(lawyer.consultation_fee)}</p>
              </div>

              <div className="space-y-3 mb-6">
                <h4 className="text-sm font-medium text-slate-700">Consultation Modes</h4>
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col items-center p-3 bg-slate-50 rounded-xl">
                    <MessageSquare className="w-5 h-5 text-slate-600" />
                    <span className="text-xs text-slate-500 mt-1">Chat</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-slate-50 rounded-xl">
                    <Phone className="w-5 h-5 text-slate-600" />
                    <span className="text-xs text-slate-500 mt-1">Call</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-slate-50 rounded-xl">
                    <Video className="w-5 h-5 text-slate-600" />
                    <span className="text-xs text-slate-500 mt-1">Video</span>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleBookConsultation}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full py-6"
                data-testid="book-consultation-btn"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Now
              </Button>

              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 leading-relaxed">
                  <strong>Disclaimer:</strong> LegalHub is a platform connecting clients with independent lawyers. 
                  We are not a law firm and do not guarantee outcomes.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Gallery Photo Modal */}
      <Dialog open={galleryModal.open} onOpenChange={(open) => setGalleryModal({ open, photo: null })}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {galleryModal.photo && (
            <div>
              <img
                src={getPhotoUrl(galleryModal.photo.path)}
                alt={galleryModal.photo.caption || 'Gallery photo'}
                className="w-full max-h-[70vh] object-contain"
              />
              {galleryModal.photo.caption && (
                <div className="p-4 bg-white border-t">
                  <p className="text-slate-700">{galleryModal.photo.caption}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default LawyerProfilePage;
